import {
    DndContext,
    closestCenter,
    TouchSensor,
    KeyboardSensor,
    MouseSensor,
    useSensor,
    useSensors,
    DragOverlay,
} from '@dnd-kit/core';

import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';

import React, { useState } from 'react';
import { DraggableItem } from '../../../../../global/components/dnd-kit/draggable-item';
import { SortableItem } from '../../../../../global/components/dnd-kit/sortable-item';
import { ButtonWrapper } from '../../../../../global/components/layout/button-wrapper';
import { DividerSection } from '../../../../../global/components/layout/divider-section';
import { useStateWithDependency } from '../../../../../global/hooks/useStateWIthDependency';
import { QuickActionItem } from '../../../../../global/models/quick-actions-model';
import { MapPortalProps } from '../../../models/arc-epi-props';
import { QuickActionItemDisplay } from './quick-action-item-display';


interface Props {
    setQuickActions: (items: QuickActionItem[]) => void;
    reordering: boolean;
    setReordering: (value: React.SetStateAction<boolean>) => any;
    selectedQuickActions: QuickActionItem[];
    portalProps: MapPortalProps;
}
export function QuickActionsReorder(props: Props) {
    const { setQuickActions, reordering, setReordering, portalProps, selectedQuickActions } = props;

    const [activeId, setActiveId] = useState<string>(null);
    const [tempActions, setTempActions] = useStateWithDependency(selectedQuickActions);

    const dndSensors = useSensors(
        useSensor(MouseSensor, {
            // Require the mouse to move by 10 pixels before activating
            activationConstraint: {
                distance: 10,
            },
        }),
        useSensor(TouchSensor, {
            // Press delay of 75ms, with tolerance of 5px of movement
            activationConstraint: {
                delay: 75,
                tolerance: 5,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    function handleDragStart(event) {
        const { active } = event;

        setActiveId(active.id);
    }

    function handleDragEnd(event) {
        const { active, over } = event;

        if (active.id !== over.id) {
            setTempActions((updatedItems: QuickActionItem[]) => {

                const oldIndex = updatedItems.findIndex(item => item.id === active.id);
                const newIndex = updatedItems.findIndex(item => item.id === over.id);

                return arrayMove(updatedItems, oldIndex, newIndex);
            });
        }

        setActiveId(null);
    }

    const onReorderingCancel = () => {
        setTempActions(selectedQuickActions);
        setReordering(false);
    };

    const onReorderingDone = () => {
        setQuickActions(tempActions);
        setReordering(false);
    };

    const renderActions = () => {
        const actions = reordering ? tempActions : selectedQuickActions;

        const actionNodes = actions.map(q => {
            return <SortableItem
                key={q.id}
                id={q.id}
                disabled={!reordering}
                active={q.id === activeId}
                node={<QuickActionItemDisplay item={q} reordering={reordering} />} />;
        });

        if (reordering) {
            return (
                <div className='quick-action-reorder'>
                    <DividerSection removeDivider>
                        <div className='quick-action-reorder-header'>
                            <h3>{portalProps?.reorderActionsHeader}</h3>
                            <p>{portalProps?.reorderActionsInstructions}</p>
                        </div>
                        <div className='quick-action-grid'>
                            {actionNodes}
                        </div>
                    </DividerSection>
                    <ButtonWrapper flexEnd>
                        <button className='btn-tertiary' type='button' onClick={onReorderingCancel}>{portalProps?.cancelText}</button>
                        <button className='btn' type='button' onClick={onReorderingDone}>{portalProps?.doneText}</button>
                    </ButtonWrapper>
                </div>
            );
        }

        return (
            <div className='quick-action-grid'>
                {actionNodes}
            </div>
        );
    };

    return (
        <>
            <DndContext
                sensors={dndSensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
                onDragStart={handleDragStart}
            >
                <SortableContext
                    items={tempActions}
                >
                    {renderActions()}
                </SortableContext>
                <DragOverlay>
                    {activeId
                        ?
                        <DraggableItem>
                            <QuickActionItemDisplay item={tempActions.find(action => action.id === activeId)} reordering={reordering} />
                        </DraggableItem>
                        : null}
                </DragOverlay>
            </DndContext>

        </>

    );

}